(() => {
  'use strict';
  const grains = new Map();
  const controllers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileFooter = window.matchMedia('(max-width: 720px)');
  const SVG_NS = 'http://www.w3.org/2000/svg';
  let frame = 0;
  let lastTime = 0;

  const canAnimate = () => document.body.dataset.animateGrains !== 'false' && !reducedMotion.matches && !document.hidden;
  function schedule() {
    if (!frame && canAnimate() && [...grains.values()].some(state => state.visible && state.animate)) {
      lastTime = 0;
      frame = requestAnimationFrame(tick);
    }
  }
  function tick(time) {
    frame = 0;
    if (!canAnimate()) { lastTime = 0; return; }
    const delta = lastTime ? Math.min((time - lastTime) / 1000, 0.05) : 0;
    lastTime = time;
    let visible = false;
    grains.forEach((state, element) => {
      if (!element.isConnected) { state.observer?.disconnect(); grains.delete(element); return; }
      if (!state.visible || !state.animate) return;
      const speed = state.speed;
      visible = true;
      state.particles.forEach(p => {
        p.x += p.vx * speed * delta;
        p.y += p.vy * speed * delta;
        if (p.x > 300 - p.r) { p.x = 300 - p.r; p.vx = -Math.abs(p.vx); }
        if (p.x < p.r) { p.x = p.r; p.vx = Math.abs(p.vx); }
        if (p.y > 300 - p.r) { p.y = 300 - p.r; p.vy = -Math.abs(p.vy); }
        if (p.y < p.r) { p.y = p.r; p.vy = Math.abs(p.vy); }
        p.circle.setAttribute('cx', p.x.toFixed(2));
        p.circle.setAttribute('cy', p.y.toFixed(2));
      });
    });
    if (visible) frame = requestAnimationFrame(tick);
  }
  function createGrain(element) {
    if (grains.has(element)) return;
    const min = Math.min(24, Math.max(1, Number(element.dataset.min) || 2));
    const max = Math.min(24, Math.max(min, Number(element.dataset.max) || 8));
    const count = Math.min(240, Math.max(1, Number(element.dataset.count) || 100));
    const color = element.dataset.color || '#57524a';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 300 300');
    svg.setAttribute('width', '100%');svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('aria-hidden', 'true');svg.setAttribute('focusable', 'false');
    const particles = [];
    for (let i = 0; i < count; i += 1) {
      const r = min + Math.random() * (max - min);
      const angle = Math.random() * Math.PI * 2;
      const velocity = 0.55 + Math.random() * 0.7;
      const circle = document.createElementNS(SVG_NS, 'circle');
      const p = {r, x: r + Math.random() * (300 - 2*r), y: r + Math.random() * (300 - 2*r), vx: Math.cos(angle)*velocity, vy: Math.sin(angle)*velocity, circle};
      circle.setAttribute('cx', p.x.toFixed(2));circle.setAttribute('cy', p.y.toFixed(2));
      circle.setAttribute('r', r.toFixed(2));circle.setAttribute('fill', color);
      circle.setAttribute('fill-opacity', (0.55+Math.random()*0.45).toFixed(2));
      svg.appendChild(circle);particles.push(p);
    }
    element.replaceChildren(svg);
    const state = { particles, visible: !('IntersectionObserver' in window), observer: null, animate: element.dataset.localAnimate !== 'false', speed: Math.max(1, Math.min(30, Number(element.dataset.speed) || Number(document.body.dataset.grainSpeed) || 10)) };
    grains.set(element, state);
    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(entries => {
        state.visible = entries[0].isIntersecting;
        schedule();
      }, {rootMargin:'40px'});
      state.observer.observe(element);
    }
    schedule();
  }
  function createHeader(header) {
    if (controllers.has(header)) return;
    const abort = new AbortController();
    const button = header.querySelector('.menu-toggle');
    const nav = header.querySelector('.primary-nav');
    const row = header.querySelector('.nav-row');
    if (!button || !nav || !row) return;
    const breakpoint = Number(header.dataset.breakpoint) || 1180;
    let layoutFrame = 0;
    const setOpen = open => {
      nav.classList.toggle('open', open);
      button.classList.toggle('open', open);
      button.setAttribute('aria-expanded', String(open));
      button.setAttribute('aria-label', open ? document.body.dataset.menuClose : document.body.dataset.menuOpen);
      if (!open) nav.querySelectorAll('details[open]').forEach(item => { item.open = false; });
    };
    const updateLayout = () => {
      layoutFrame = 0;
      // Measure the natural desktop row before falling back to a menu button.
      header.dataset.menuMode = 'desktop';
      let mobile = window.innerWidth < breakpoint;
      if (!mobile) {
        const children = [...row.children];
        const style = getComputedStyle(row);
        const required = children.reduce((total, child) => total + child.getBoundingClientRect().width, 0) + parseFloat(style.columnGap || 0) * (children.length - 1);
        const available = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        mobile = required > available + 1;
      }
      header.dataset.menuMode = mobile ? 'mobile' : 'desktop';
      if (!mobile) setOpen(false);
    };
    const queueLayout = () => { if (!layoutFrame) layoutFrame = requestAnimationFrame(updateLayout); };
    button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'), { signal: abort.signal });
    nav.addEventListener('click', event => { if (event.target.closest('a')) setOpen(false); }, { signal: abort.signal });
    header.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        const wasOpen = button.getAttribute('aria-expanded') === 'true';
        setOpen(false);
        if (wasOpen) button.focus();
      }
      if (event.key === 'Tab' && button.getAttribute('aria-expanded') === 'true') {
        const targets = [button, ...nav.querySelectorAll('a[href],summary')].filter(el => el.getClientRects().length);
        const i = targets.indexOf(document.activeElement);
        if (i >= 0) { event.preventDefault(); targets[(i + (event.shiftKey ? -1 : 1) + targets.length) % targets.length].focus(); }
      }
    }, { signal: abort.signal });
    document.addEventListener('click', event => { if (!header.contains(event.target)) setOpen(false); }, { signal: abort.signal });
    window.addEventListener('resize', queueLayout, { signal: abort.signal, passive: true });
    const observer = new ResizeObserver(queueLayout);
    observer.observe(row);
    document.fonts.ready.then(() => { if (!abort.signal.aborted) queueLayout(); });
    updateLayout();
    controllers.set(header, () => { abort.abort(); observer.disconnect(); cancelAnimationFrame(layoutFrame); });
  }
  function createFooter(footer) {
    if (controllers.has(footer)) return;
    const abort = new AbortController();
    const panels = footer.querySelectorAll('[data-footer-panel]');
    const update = () => panels.forEach(panel => {
      const collapsible = mobileFooter.matches && footer.dataset.footerCollapse === 'true';
      panel.open = !collapsible;
      panel.querySelector('summary').tabIndex = collapsible ? 0 : -1;
    });
    panels.forEach(panel => panel.querySelector('summary').addEventListener('click', e => {
      if (!mobileFooter.matches || footer.dataset.footerCollapse !== 'true') e.preventDefault();
    }, {signal:abort.signal}));
    mobileFooter.addEventListener('change', update);update();
    controllers.set(footer, () => {abort.abort();mobileFooter.removeEventListener('change', update);});
  }
  function refreshAnchors(excludedScope) {
    document.querySelectorAll('[data-gcc-anchor]').forEach(marker => marker.remove());
    const used = new Set([...document.querySelectorAll('[id]')].map(el => el.id));
    document.querySelectorAll('[data-gcc-section][data-anchor]').forEach(section => {
      const name = section.dataset.anchor;
      if (!name || used.has(name) || excludedScope?.contains(section) || !section.getClientRects().length) return;
      const marker = document.createElement('span');
      marker.id = name;
      marker.className = 'gcc-anchor';
      marker.dataset.gccAnchor = '';
      marker.setAttribute('aria-hidden', 'true');
      section.prepend(marker);
      used.add(name);
    });
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-grain]').forEach(createGrain);
    scope.querySelectorAll('[data-gcc-header]').forEach(createHeader);
    scope.querySelectorAll('[data-footer-collapse]').forEach(createFooter);
    refreshAnchors();
    const now = new Date();
    const localToday = [now.getFullYear(), String(now.getMonth()+1).padStart(2,'0'), String(now.getDate()).padStart(2,'0')].join('-');
    scope.querySelectorAll('[data-delivery-date]').forEach(input => { input.min = localToday; });
  }
  function destroy(scope) {
    grains.forEach((state, el) => {if (scope.contains(el)) {state.observer?.disconnect();grains.delete(el);}});
    controllers.forEach((cleanup, el) => {if (scope.contains(el)) {cleanup();controllers.delete(el);}});
  }
  reducedMotion.addEventListener('change', schedule);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => { destroy(e.target); refreshAnchors(e.target); });
  document.addEventListener('shopify:section:reorder', () => refreshAnchors());
  mobileFooter.addEventListener('change', () => refreshAnchors());
  window.addEventListener('load', () => {
    if (!location.hash) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (_) { return; }
    if (target?.hasAttribute('data-gcc-anchor')) target.scrollIntoView();
  }, { once: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();
// Variant controls use Shopify's standard product form; checkout is handled by Shopify.
document.addEventListener('change', event => {
  if (!event.target.matches('[data-product-variant]')) return;
  const root = event.target.closest('[data-product-root]');
  const option = event.target.selectedOptions[0];
  if (!root || !option) return;
  root.querySelector('[data-current-price]').textContent = option.dataset.price;
  const button = root.querySelector('[data-add-to-cart]');
  button.disabled = option.dataset.available !== 'true';
  button.textContent = button.disabled ? 'Sold out' : 'Add to cart';
});
